'use client';

import type { Ref, ReactNode } from 'react';

import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';

import { LINE, RADIUS, LOCK_ICON } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';

import { summarizeSelection } from './hub-condition-summary';

/**
 * 허브의 **조건 고르개** — 칩과 같은 결의 알약 하나.
 *
 * ⚠️ MUI 기본값을 세 군데 덮어야 칩처럼 보인다(안 덮으면 넓고 붕 뜬 알약이 된다).
 * ```
 *   .MuiSelect-select   기본 min-height 와 좌우 14/32px 여백 → 0 으로 눕히고 12/26px 로 다시 준다
 *   .MuiSelect-icon     테마가 right:10 · 18px → 6px · 16px 로 당긴다(글자와 화살표 사이 빈틈 제거)
 *   notchedOutline      테마 기본이 회색 20% 라 흐리다 → 또렷한 선으로
 * ```
 * ⚠️ **폭을 지정하지 않는다** — 글자만큼만 차지해야 여러 개가 한 줄에 가지런히 선다.
 *   조건 칸(사이드바)처럼 한 칸을 가득 채우는 자리는 `block` 으로 **네모난 입력칸 꼴**로 바꾼다 —
 *   알약을 그대로 늘이면 화살표만 저 끝에 떨어져 붕 뜬다.
 *
 * 🔑 **여럿 고르는 칸(`multiple`)은 고른 것을 「첫 이름 외 N」으로 줄여 적는다** — 소재 찾기 툴바와
 *   같은 말투다. `prefix`(「채널:」)를 주면 앞말이 흐리게 붙는다.
 * ⚠️ `multiple` 에서 `onChange` 는 **누른 항목 하나**를 준다(목록 전체가 아니다) — 부르는 쪽이 켜고 끈다.
 */
/**
 * 고를 수 있는 한 칸.
 * ⚠️ `node` 는 **펼친 목록에만** 쓴다 — 접힌 칸은 「첫 이름 외 N」으로 줄여 적어야 해서 글자(`label`)가 필요하다.
 * 🔑 `group` 을 주면 **그 말이 바뀔 때마다** 머리줄이 선다 — 목록을 미리 묶어 넘겨야 한다
 *   (여기서 다시 정렬하지 않는다 — 부르는 쪽이 정한 차례가 뜻을 가진다).
 * ⚠️ `disabled` 는 **자리를 남기고 못 고르게** 두는 것이다 — 아예 빼면 왜 없는지 알 길이 없다.
 * 🔑 `locked` 는 **자리를 남기고 눌리게** 둔다(플랜) — 이름 뒤에 자물쇠(`LOCK_ICON`)가 붙고, 누르면 `onChange` 가 아니라
 *   `onLocked` 가 불린다(고른 값은 그대로다). 부르는 쪽이 거기서 업그레이드를 안내한다.
 */
export type HubSelectOption = {
  value: string;
  label: string;
  node?: ReactNode;
  group?: string;
  disabled?: boolean;
  locked?: boolean;
  /** 패싯 수 — 필터 판의 칩에 이름 옆으로 선다(`toCountedOptions` 가 채운다) · 고르개 목록은 `node` 로 그린다 */
  count?: number;
};

export function HubSelect({
  value,
  onChange,
  options,
  empty,
  hideEmpty = false,
  disabled = false,
  multiple = false,
  block = false,
  prefix,
  renderLabel,
  onLocked,
  active,
  extra,
  rootRef,
}: {
  /** 하나 고를 때는 문자열, 여럿이면 문자열 목록 */
  value: string | string[];
  onChange: (next: string) => void;
  options: HubSelectOption[];
  /** 아무것도 안 골랐을 때의 글자 */
  empty: string;
  /** 「전체」가 없는 칸(정렬처럼 늘 하나를 고르는 자리) */
  hideEmpty?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  /** 한 칸을 가득 채우는 입력칸 꼴 — 조건 칸(사이드바)의 자리 */
  block?: boolean;
  /** 앞말 — 「채널:」처럼 무엇을 고르는 칸인지 흐리게 적는다 */
  prefix?: string;
  /** 고른 것을 어떻게 적을지 — 없으면 고른 항목의 이름 */
  renderLabel?: (value: string | string[]) => ReactNode;
  /** 잠긴 칸(`locked`)을 눌렀을 때 — 값은 바뀌지 않는다 */
  onLocked?: (value: string) => void;
  /**
   * 걸린 조건으로 칠할지 — 주면 값으로 짐작하지 않고 이것을 따른다.
   * 🔑 `hideEmpty` 칸 가운데 **늘 걸려 있는 조건**(채널·기간)은 `true`, 「전체」를 값으로 가진 칸(발굴·형식)은
   *   「전체가 아닌가」를 준다. 정렬·보기 고르개는 주지 않는다 — 조건이 아니다.
   */
  active?: boolean;
  /**
   * 목록 맨 아래 «값이 아닌» 한 줄 — 「직접 선택…」처럼 다른 고르개를 여는 자리. 고른 값은 바뀌지 않는다.
   * 🔑 `MenuItem` 의 `onClick` 으로 연다 — `Select` 의 `onChange` 는 값이 바뀔 때만 불린다.
   */
  extra?: { label: string; icon?: string; locked?: boolean; onClick: () => void };
  /** 접힌 칸의 뿌리 — 다른 팝오버(달력)를 이 밑에 붙일 닻 */
  rootRef?: Ref<HTMLDivElement>;
}) {
  /** 누른 칸 하나 — 잠긴 칸이면 `onLocked` 로 돌린다 */
  const pick = (next: string) => {
    if (next === EXTRA_VALUE) return;
    if (options.some((option) => option.value === next && option.locked)) {
      onLocked?.(next);
      return;
    }
    onChange(next);
  };

  /** 골라서 «좁혀진» 칸인가 — 늘 값이 있는 칸(정렬)은 `active` 를 안 주면 강조하지 않고 글자만 또렷하게 둔다 */
  const picked = active ?? (Array.isArray(value) ? value.length > 0 : Boolean(value) && !hideEmpty);
  const textColor = picked ? 'primary.dark' : hideEmpty ? 'text.primary' : 'text.secondary';

  return (
    <Select
      ref={rootRef}
      size="small"
      displayEmpty
      disabled={disabled}
      multiple={multiple}
      value={value}
      onChange={(e) => !multiple && pick(String(e.target.value))}
      renderValue={(current) => {
        const body = renderLabel
          ? renderLabel(current as string | string[])
          : summarizeSelection(current as string | string[], options, empty);
        if (!prefix) return body;
        return (
          <>
            <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, mr: 0.5 }}>{prefix}</Box>
            {body}
          </>
        );
      }}
      MenuProps={{ PaperProps: { sx: { mt: 0.5, borderRadius: RADIUS.md, maxHeight: 360 } } }}
      sx={{
        height: block ? 34 : 28,
        width: block ? 1 : 'auto',
        borderRadius: block ? RADIUS.sm : RADIUS.pill,
        bgcolor: picked ? 'primary.lighter' : 'background.paper',
        color: textColor,
        '& .MuiSelect-select': {
          display: 'flex',
          alignItems: 'center',
          minHeight: 'unset !important',
          height: block ? 34 : 28,
          boxSizing: 'border-box',
          padding: block ? '0 28px 0 10px !important' : '0 24px 0 12px !important',
          typography: 'uiAction',
          lineHeight: 1,
        },
        '& .MuiSelect-icon': { right: block ? 8 : 6, width: 16, height: 16, top: 'calc(50% - 8px)', color: 'inherit', opacity: 0.6 },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: picked ? 'primary.main' : LINE.edge },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: picked ? 'primary.main' : 'text.disabled' },
      }}
    >
      {!hideEmpty && !multiple && <MenuItem value="" sx={ITEM_SX}>{empty}</MenuItem>}
      {options.flatMap((option, index) => [
        /* 묶음 머리 — 앞 칸과 묶음 이름이 다를 때만 선다(화살표는 머리를 건너뛴다: 누를 수 없는 줄이다) */
        option.group && option.group !== options[index - 1]?.group ? (
          <ListSubheader key={`group:${option.group}`} sx={GROUP_SX}>
            {option.group}
          </ListSubheader>
        ) : null,
        <MenuItem
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          onClick={multiple ? () => pick(option.value) : undefined}
          sx={{ ...(option.group ? GROUPED_ITEM_SX : ITEM_SX), ...(option.locked ? LOCKED_ITEM_SX : null) }}
        >
          {multiple && <Checkbox size="small" checked={(value as string[]).includes(option.value)} sx={{ p: 0.5, mr: 1 }} />}
          {option.node ?? option.label}
          {option.locked && <Iconify icon={LOCK_ICON} width={14} sx={{ ml: 0.5, flexShrink: 0 }} />}
        </MenuItem>,
      ])}
      {extra && (
        <MenuItem value={EXTRA_VALUE} onClick={extra.onClick} sx={{ ...ITEM_SX, ...EXTRA_ITEM_SX, ...(extra.locked ? LOCKED_ITEM_SX : null) }}>
          {extra.icon && <Iconify icon={extra.icon} width={14} sx={{ mr: 1, flexShrink: 0 }} />}
          {extra.label}
          {extra.locked && <Iconify icon={LOCK_ICON} width={14} sx={{ ml: 0.5, flexShrink: 0 }} />}
        </MenuItem>
      )}
    </Select>
  );
}

// ---- 이하 비공개 ----------------------------------------------------------

const ITEM_SX = { typography: 'uiBody', minHeight: 32, py: 0.5 } as const;

const GROUP_SX = {
  typography: 'uiMeta',
  fontWeight: 700,
  color: 'text.secondary',
  letterSpacing: '.04em',
  lineHeight: 2.2,
  bgcolor: 'transparent',
} as const;

/** 묶음에 든 줄 — 머리줄보다 한 칸 들어가야 위아래가 갈린다 */
const GROUPED_ITEM_SX = { ...ITEM_SX, pl: 3 } as const;

/** `extra` 줄의 값 — 옵션 값과 겹치지 않는 자리표시 */
const EXTRA_VALUE = '__hub-select-extra__';

/** `extra` 줄 — 값 목록과 가는 선으로 갈린다 */
const EXTRA_ITEM_SX = { borderTop: `1px solid ${LINE.hair}`, mt: 0.5 } as const;

/** 잠긴 줄 — 흐리게 적지만 눌린다(`disabled` 와 달리 손가락이 그대로다) */
const LOCKED_ITEM_SX = { color: 'text.disabled' } as const;
